import { z } from "zod";

/*
 * No foetal-exam field is required. The MN-CMS spec makes none of them mandatory — its mandatory
 * list is Agreed EDD, EDD Method, Gravida, Parity and the two blood pressures — and the backend
 * only checks that each baby in a multiple pregnancy has an observation row.
 *
 * Engagement, Activity and Heart heard were dropped in KIN-2042. Presentation was held back there
 * as "required from 28 weeks" because the Designer Brief asks for it; Eleanor has since confirmed
 * in review that it should be optional too, which deliberately overrides the brief on that point.
 * Please don't restore the requirement from the brief. Presentation is now shown at every
 * gestation too (see FoetalObservationSection), with the 28-week timing given as an info hint.
 *
 * Engagement was the worst of them: demanded from week 0, when it is not assessable until roughly
 * 34, which leaves a GP entering something untrue to get past the form — and that then goes to the
 * hospital.
 */
const foetalObservationSchema = z
  .object({
    foetusId: z.union([z.string(), z.number()]).nullish(),
    presentation: z.string().optional(),
    engagement: z.string().optional(),
    heartHeardWithDoppler: z.string().optional(),
    activity: z.string().optional(),
    heartRateBpm: z.string().optional(),
    estimatedWeightGrams: z.string().optional(),
    notes: z.string().optional(),
  })
  .partial();

export const antenatalFormSchema = z.object({
  gestationWeeks: z.string().optional(),
  gestationDays: z.string().optional(),
  bloodPressureSystolic: z.string().min(1, "BP Systolic is required"),
  bloodPressureDiastolic: z.string().min(1, "BP Diastolic is required"),
  maternalWeightKg: z.string().optional(),
  maternalHeightCm: z.string().optional(),
  urinalysisGlucose: z.string().optional(),
  urinalysisProtein: z.string().optional(),
  // Graded, not yes/no — a dipstick reads a level and a tick box throws it away (KIN-2038).
  // Nitrites is a dropdown too now, so it holds the stored code rather than a boolean; it is
  // Negative/Positive only, and stays optional because a visit may simply not have recorded it.
  urinalysisKetones: z.string().optional(),
  urinalysisBlood: z.string().optional(),
  urinalysisLeucocytes: z.string().optional(),
  urinalysisNitrites: z.string().optional(),
  ankleOedema: z.string().optional(),
  fundalHeightCm: z.string().optional(),
  uterineContractions: z.string().optional(),
  bmi: z.string().optional(),
  haemoglobin: z.string().optional(),
  dateOfNextVisitWeeks: z.string().optional(),
  impression: z.string().optional(),
  plan: z.string().optional(),
  additionalInfo: z.string().optional(),
  // Keyed by foetusIdentifier — same shape buildInitialObsState produces.
  foetalObservations: z.record(foetalObservationSchema).optional(),
});
